import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useLazyQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { Box, TextField, Typography, CircularProgress, InputAdornment } from '@mui/material';
import Button from '../../components/CustomButtons/Button';
import Tile from '../../components/Tile/Tile';
import { useNotifications } from 'modules/notification';
import { UPDATE_COMPANY_INFO } from '../CompanyProfile/api';

const CHECK_MAGIC_EMAIL_AVAILABILITY = gql`
  query CheckMagicEmailAvailability($email: String!) {
    checkMagicEmailAvailability(email: $email) {
      available
      error
    }
  }
`;

const MAGIC_EMAIL_DOMAIN = '@magic.trnsact.com';

export const MagicEmailInboxCard = () => {
  const account = useSelector(state => state.account);
  const { showNotification } = useNotifications();

  const existingEmail = account.magicEmailAddress || '';
  const isAlreadySet = Boolean(existingEmail);

  const [dealerName, setDealerName] = useState('');
  const [availability, setAvailability] = useState(null); // null | 'available' | 'unavailable'

  const fullEmail = dealerName ? `${dealerName}${MAGIC_EMAIL_DOMAIN}` : '';

  const [checkAvailability, { loading: checking }] = useLazyQuery(CHECK_MAGIC_EMAIL_AVAILABILITY, {
    fetchPolicy: 'no-cache',
    onCompleted(data) {
      const result = data?.checkMagicEmailAvailability;
      setAvailability(result?.available ? 'available' : 'unavailable');
    },
    onError() {
      showNotification('Error checking availability', { type: 'error' });
    },
  });

  const [updateAccount, { loading: saving }] = useMutation(UPDATE_COMPANY_INFO, {
    context: { authRequired: true },
    onCompleted() {
      showNotification('Magic email address saved');
    },
    onError(err) {
      showNotification(err.message || 'Error saving email address', { type: 'error' });
    },
  });

  const handleCheck = () => {
    if (!dealerName.trim()) return;
    setAvailability(null);
    checkAvailability({ variables: { email: fullEmail } });
  };

  const handleSave = () => {
    updateAccount({
      variables: {
        dynamicsId: account.dynamicsAccountId,
        fieldsObj: { magicEmailAddress: fullEmail },
      },
    });
  };

  return (
    <Tile title="Magic Email Inbox">
      {isAlreadySet ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Magic email address
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {existingEmail}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            This address cannot be changed once set.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Set a unique magic email inbox address for this dealer. Once saved, it cannot be changed.
          </Typography>
          <TextField
            label="Dealer name"
            value={dealerName}
            onChange={e => {
              setDealerName(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''));
              setAvailability(null);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="body2" color="text.secondary">
                    {MAGIC_EMAIL_DOMAIN}
                  </Typography>
                </InputAdornment>
              ),
            }}
            helperText={
              availability === 'available'
                ? '✓ Address is available'
                : availability === 'unavailable'
                  ? 'Email address not available.'
                  : fullEmail || ' '
            }
            FormHelperTextProps={{
              sx: {
                color:
                  availability === 'available'
                    ? 'success.main'
                    : availability === 'unavailable'
                      ? 'error.main'
                      : 'text.secondary',
              },
            }}
            size="small"
            sx={{ maxWidth: 380 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button color="info" size="sm" onClick={handleCheck} disabled={!dealerName.trim() || checking}>
              {checking ? <CircularProgress size={16} /> : 'Check Availability'}
            </Button>
            <Button color="primary" size="sm" onClick={handleSave} disabled={availability !== 'available' || saving}>
              {saving ? <CircularProgress size={16} /> : 'Save'}
            </Button>
          </Box>
        </Box>
      )}
    </Tile>
  );
};
